import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { NhlSeasonStats } from '../../lib/api/nhlPlayers';
import { translate } from '../../lib/translations';

interface PerformanceChartProps {
  seasonStats: NhlSeasonStats | null;
  isGoalie: boolean;
  language: 'fr' | 'en';
}

function formatSeasonLabel(season: number): string {
  const s = season.toString();
  if (s.length === 8) {
    return `${s.slice(0, 4)}-${s.slice(6)}`;
  }
  return s;
}

export function PerformanceChart({ seasonStats, isGoalie, language }: PerformanceChartProps) {
  if (!seasonStats?.seasonTotals?.length) {
    return (
      <div className="text-muted text-center py-4">
        {translate('player.detail.no.season.data', language)}
      </div>
    );
  }

  // Only keep NHL regular season entries (gameTypeId === 2)
  const regularSeasonNhl = seasonStats.seasonTotals
    .filter((s) => s.gameTypeId === 2 && s.leagueAbbrev === 'NHL')
    .sort((a, b) => a.season - b.season);

  if (regularSeasonNhl.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        {translate('player.detail.no.season.data', language)}
      </div>
    );
  }

  if (isGoalie) {
    const data = regularSeasonNhl.map((s) => ({
      season: formatSeasonLabel(s.season),
      [translate('stat.wins', language)]: s.wins ?? 0,
      [translate('stat.losses', language)]: s.losses ?? 0,
      [translate('stat.shutouts', language)]: s.shutouts ?? 0,
      [translate('stat.gamesPlayed', language)]: s.gamesPlayed ?? 0,
    }));

    const winsKey = translate('stat.wins', language);
    const lossesKey = translate('stat.losses', language);
    const shutoutsKey = translate('stat.shutouts', language);
    const gpKey = translate('stat.gamesPlayed', language);

    return (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
          <XAxis dataKey="season" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey={gpKey} fill="#6c757d" radius={[3, 3, 0, 0]} />
          <Bar dataKey={winsKey} fill="#198754" radius={[3, 3, 0, 0]} />
          <Bar dataKey={lossesKey} fill="#dc3545" radius={[3, 3, 0, 0]} />
          <Bar dataKey={shutoutsKey} fill="#0dcaf0" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Skater chart
  const data = regularSeasonNhl.map((s) => ({
    season: formatSeasonLabel(s.season),
    [translate('stat.goals', language)]: s.goals ?? 0,
    [translate('stat.assists', language)]: s.assists ?? 0,
    [translate('stat.points', language)]: s.points ?? 0,
    [translate('stat.gamesPlayed', language)]: s.gamesPlayed ?? 0,
  }));

  const goalsKey = translate('stat.goals', language);
  const assistsKey = translate('stat.assists', language);
  const pointsKey = translate('stat.points', language);
  const gpKey = translate('stat.gamesPlayed', language);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis dataKey="season" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey={gpKey} fill="#6c757d" radius={[3, 3, 0, 0]} />
        <Bar dataKey={goalsKey} fill="#dc3545" radius={[3, 3, 0, 0]} />
        <Bar dataKey={assistsKey} fill="#0d6efd" radius={[3, 3, 0, 0]} />
        <Bar dataKey={pointsKey} fill="#fd7e14" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
